import React from "react";
import { IconCheck, IconX, IconRobot, IconTool } from "@tabler/icons-react";
import { Badge, Card, CardContent, CardHeader, Stack, Markdown } from "@altimateai/lego";
import { TurnResult } from "../types";
import { getScoreColorClasses } from "../utils";
import MarkdownErrorBoundary from "./MarkdownErrorBoundary";
import { StrengthsWeaknesses } from "./StrengthsWeaknesses";

interface TurnResultCardProps {
  turn: TurnResult;
  variant?: "detailed" | "compact";
}

export const TurnResultCard: React.FC<TurnResultCardProps> = ({ turn, variant = "detailed" }) => {
  const isCompact = variant === "compact";

  if (!turn.evaluation) {
    return (
      <div className="al-w-full al-overflow-hidden">
        <Card>
          <CardContent className="al-p-3">
            <div className="al-text-sm al-text-gray-500">
              Turn {turn.turn_number} — Evaluation data not available
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const scoreColors = getScoreColorClasses(turn.evaluation.overall_score);
  const padding = isCompact ? "al-p-2" : "al-p-3";
  const textSize = isCompact ? "al-text-xs" : "al-text-sm";

  return (
    <div className="al-w-full al-overflow-hidden">
      <Card className={`al-w-full ${isCompact ? "al-bg-white al-border-gray-300" : ""}`}>
        <CardHeader className={isCompact ? "al-pb-2 al-pt-3" : "al-pb-3"}>
          <Stack direction="row" justify="between" align="center">
            <div className={`${textSize} al-font-semibold`}>Turn {turn.turn_number}</div>
            <div className={isCompact ? "" : "al-pointer-events-none"}>
              <Badge className={scoreColors.badge}>{turn.evaluation.overall_score}/5</Badge>
            </div>
          </Stack>
        </CardHeader>
        <CardContent className={isCompact ? "al-pt-2" : ""}>
          <div className={isCompact ? "al-space-y-2" : "al-space-y-3"}>
            {/* User Input */}
            <div>
              <div
                className={`al-text-xs al-font-semibold al-text-orange-700 al-uppercase ${isCompact ? "al-mb-1" : "al-mb-2"}`}
              >
                User Input
              </div>
              <div
                className={`al-bg-orange-50 al-border al-border-orange-200 ${padding} al-rounded-lg ${textSize} al-max-h-32 al-overflow-y-auto al-overflow-x-hidden al-break-words`}
              >
                {turn.user_message}
              </div>
            </div>

            {/* Expected AI Response */}
            <div>
              <div
                className={`al-text-xs al-font-semibold al-text-blue-700 al-uppercase ${isCompact ? "al-mb-1" : "al-mb-2"}`}
              >
                Expected AI Response
              </div>
              <div
                className={`al-bg-blue-50 al-border al-border-blue-200 ${padding} al-rounded-lg ${textSize} al-max-h-32 al-overflow-y-auto al-overflow-x-hidden al-break-words`}
              >
                {turn.expected_ai_response}
              </div>
            </div>

            {/* Actual AI Response */}
            <div>
              <div
                className={`al-text-xs al-font-semibold al-uppercase ${isCompact ? "al-mb-1" : "al-mb-2"} ${scoreColors.text}`}
              >
                Actual AI Response
              </div>
              <div
                className={`al-border ${isCompact ? "al-p-3" : "al-p-4"} al-rounded-lg ${textSize} al-prose al-prose-sm al-max-w-none al-max-h-64 al-overflow-y-auto al-overflow-x-hidden ${scoreColors.bg}`}
              >
                <MarkdownErrorBoundary fallbackContent={turn.actual_ai_response ?? ""}>
                  <Markdown content={turn.actual_ai_response ?? ""} />
                </MarkdownErrorBoundary>
              </div>
            </div>

            {/* Agent Intermediate Steps */}
            {turn.intermediate_messages && (
              <div>
                <Stack
                  direction="row"
                  align="center"
                  gap="sm"
                  className={isCompact ? "al-mb-1" : "al-mb-2"}
                >
                  <IconTool
                    className={`${isCompact ? "al-w-4 al-h-4" : "al-w-4 al-h-4"} al-text-indigo-700`}
                  />
                  <div className="al-text-xs al-font-semibold al-text-indigo-700 al-uppercase">
                    Agent Intermediate Steps
                  </div>
                </Stack>
                <div
                  className={`al-bg-indigo-50 al-border al-border-indigo-200 ${isCompact ? "al-p-3" : "al-p-4"} al-rounded-lg al-max-h-96 al-overflow-y-auto al-overflow-x-hidden`}
                >
                  <pre
                    className={`${textSize} al-text-gray-700 al-whitespace-pre-wrap al-break-words al-font-mono al-m-0`}
                  >
                    {turn.intermediate_messages}
                  </pre>
                </div>
              </div>
            )}

            {/* AI Judge Evaluation */}
            <div className="al-border-t al-pt-3">
              <Stack direction="row" align="center" gap="sm" className="al-mb-3">
                <IconRobot
                  className={`${isCompact ? "al-w-4 al-h-4" : "al-w-5 al-h-5"} al-text-purple-700`}
                />
                <div
                  className={`${isCompact ? "al-text-sm" : "al-text-lg"} al-font-semibold al-text-purple-700`}
                >
                  AI Judge Evaluation
                </div>
              </Stack>

              {/* Criteria Breakdown */}
              {turn.evaluation.criteria_scores &&
                Object.keys(turn.evaluation.criteria_scores).length > 0 && (
                  <div className={`${isCompact ? "al-mb-2" : "al-mb-3"}`}>
                    <div
                      className={`${isCompact ? "al-text-xs" : "al-text-sm"} al-font-semibold al-text-gray-700 al-uppercase ${isCompact ? "al-mb-1" : "al-mb-2"}`}
                    >
                      Criteria Details
                    </div>
                    <div className="al-space-y-2">
                      {Object.entries(turn.evaluation.criteria_scores).map(
                        ([criterionName, criterionData]) => {
                          const criterionColors = getScoreColorClasses(criterionData.score);
                          return (
                            <div
                              key={criterionName}
                              className={`al-bg-gray-50 al-border al-border-gray-200 ${isCompact ? "al-p-2" : "al-p-3"} al-rounded-lg`}
                            >
                              <Stack
                                direction="row"
                                justify="between"
                                align="start"
                                className="al-mb-2"
                              >
                                <div
                                  className={`${isCompact ? "al-text-xs" : "al-text-sm"} al-font-semibold al-text-gray-800`}
                                >
                                  {criterionName}
                                </div>
                                <Stack direction="row" gap="xs" align="center">
                                  <Badge className={criterionColors.badge}>
                                    {criterionData.score}/5
                                  </Badge>
                                  <Badge
                                    className={
                                      criterionData.pass
                                        ? "!al-bg-green-100 !al-text-green-700 al-border al-border-green-200"
                                        : "!al-bg-red-100 !al-text-red-700 al-border al-border-red-200"
                                    }
                                  >
                                    {criterionData.pass ? (
                                      <IconCheck className="al-w-3 al-h-3 al-inline" />
                                    ) : (
                                      <IconX className="al-w-3 al-h-3 al-inline" />
                                    )}
                                    {criterionData.pass ? " Pass" : " Fail"}
                                  </Badge>
                                </Stack>
                              </Stack>
                              <div
                                className={`${isCompact ? "al-text-xs" : "al-text-sm"} al-text-gray-600 al-break-words`}
                              >
                                {criterionData.explanation}
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}

              {/* Overall Reasoning */}
              {turn.evaluation.reasoning && (
                <div
                  className={`al-bg-purple-50 al-border al-border-purple-200 ${isCompact ? "al-p-3" : "al-p-4"} al-rounded-lg ${isCompact ? "al-mb-2" : "al-mb-3"}`}
                >
                  <div
                    className={`${isCompact ? "al-text-xs" : "al-text-sm"} al-font-semibold al-text-purple-700 al-uppercase al-mb-2`}
                  >
                    Overall Reasoning
                  </div>
                  <div
                    className={`${isCompact ? "al-text-xs" : "al-text-sm"} al-text-gray-700 al-italic al-break-words`}
                  >
                    {turn.evaluation.reasoning}
                  </div>
                </div>
              )}

              <StrengthsWeaknesses
                strengths={turn.evaluation.strengths}
                weaknesses={turn.evaluation.weaknesses}
                isCompact={isCompact}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
